import api from "./api";

/**
 * Professor Activity Service - API calls for Professor Activity Review & Reactions
 */

// Helper to get JWT/Token header
const getAuthHeaders = () => {
  const token = localStorage.getItem("token");
  return {
    Authorization: `Bearer ${token}`,
    "Content-Type": "application/json",
  };
};

/**
 * Fetch assigned / available students for professor review
 */
export const fetchAssignedStudents = async () => {
  try {
    const res = await api.get("/auth/professor/students/");
    return res.data?.students || [];
  } catch (err) {
    console.error("Error fetching assigned students:", err);
    throw err;
  }
};

/**
 * Fetch student activities for professor review
 */
export const fetchStudentActivities = async (studentId, params = {}) => {
  try {
    const queryParams = new URLSearchParams();
    if (params.page) queryParams.append("page", params.page);
    if (params.page_size) queryParams.append("page_size", params.page_size);
    if (params.type && params.type !== "all") queryParams.append("type", params.type);
    if (params.status && params.status !== "all") queryParams.append("status", params.status);
    if (params.date_from) queryParams.append("date_from", params.date_from);
    if (params.date_to) queryParams.append("date_to", params.date_to);

    let res;
    try {
      res = await api.get(`/auth/professor/student-activities/${studentId}/?${queryParams.toString()}`);
    } catch (e) {
      res = await api.get(`/auth/student/activities/${studentId}/?${queryParams.toString()}`);
    }

    const list = res.data?.activities || res.data?.results || (Array.isArray(res.data) ? res.data : []);
    const count = res.data?.total_count || res.data?.count || list.length;

    return {
      activities: list,
      total_count: count,
      total_pages: res.data?.total_pages || Math.ceil(count / (params.page_size || 25)) || 1,
    };
  } catch (err) {
    console.error("Error fetching student activities:", err);
    throw err;
  }
};

/**
 * Add / Update reaction (✅ Approved / ❌ Rejected) on an activity
 */
export const addActivityReaction = async (activityId, reactionData) => {
  try {
    const res = await api.post("/auth/activity/reaction/", {
      activity_id: activityId,
      type: reactionData.type,
      signature: reactionData.signature,
      from_source: reactionData.from_source || "Professor",
    });
    return res.data;
  } catch (err) {
    console.error("Error saving reaction:", err);
    throw err;
  }
};

/**
 * Add comment / grade assessment to an activity
 */
export const addActivityComment = async (activityId, commentData) => {
  try {
    const res = await api.post("/auth/activity/comment/", {
      activity_id: activityId,
      text: commentData.text || "",
      rating: commentData.rating || null,
      numericScore: commentData.numericScore || null,
      facultyDecision: commentData.facultyDecision || null,
      status: commentData.status || null,
      signature: commentData.signature || null,
      from_source: commentData.from_source || "Professor",
    });
    return res.data;
  } catch (err) {
    console.error("Error saving comment:", err);
    throw err;
  }
};

/**
 * Bulk reactions (Approve / Reject multiple activities)
 */
export const addBulkActivityReactions = async (activityIds, reactionData) => {
  try {
    const res = await api.post("/auth/student/bulk-activity-reaction/", {
      activity_ids: activityIds,
      reaction: reactionData,
    });
    return res.data;
  } catch (err) {
    console.error("Error submitting bulk reactions:", err);
    throw err;
  }
};

/**
 * Fetch professor saved signature
 */
export const fetchProfessorSignature = async () => {
  try {
    const res = await api.get("/auth/professor/signature/");
    return res.data?.signature || null;
  } catch (err) {
    console.error("Error fetching signature:", err);
    return null;
  }
};

/**
 * Save professor signature
 */
export const saveProfessorSignature = async (signatureData) => {
  try {
    const res = await api.post("/auth/professor/signature/", {
      signature: signatureData,
    });
    return res.data;
  } catch (err) {
    console.error("Error saving signature:", err);
    throw err;
  }
};

export default {
  fetchAssignedStudents,
  fetchStudentActivities,
  addActivityReaction,
  addActivityComment,
  addBulkActivityReactions,
  fetchProfessorSignature,
  saveProfessorSignature,
};
